import React, { useEffect, useMemo, useState } from "react";
import { Upload } from "react-feather";

const LogoUploader = ({ selectedFile, onChange, errors, onBlur }) => {
  const [previewUrl, setPreviewUrl] = useState("");

  useEffect(() => {
    if (selectedFile) {
      const objectUrl = URL.createObjectURL(selectedFile);
      setPreviewUrl(objectUrl);

      return () => URL.revokeObjectURL(objectUrl); // Cleanup
    } else {
      setPreviewUrl("https://placehold.co/128x128/e2e8f0/adb5bd?text=Logo");
    }
  }, [selectedFile]);

  return (
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-1">
        Brand Logo <span className="text-red-500">*</span>
      </label>
      <div className={`flex items-center gap-4 px-4 py-3 border ${errors?.sBrandLogo ? "border-red-500 bg-red-50" : "border-gray-200 bg-gray-50"} rounded-lg`}>
        <img
          src={previewUrl}
          alt="Logo preview"
          className="h-16 w-16 rounded-md object-contain bg-gray-100 p-1"
        />
        <label className="flex items-center px-4 py-2 bg-white border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 hover:bg-gray-50 cursor-pointer">
          <Upload size={16} className="mr-2" />
          Upload Logo
          <input
            type="file"
            accept="image/*"
            onChange={onChange}
            className="sr-only"
            onBlur={() => onBlur("sBrandLogo")}
          />
        </label>
      </div>
      {errors?.sBrandLogo && (
        <p className="text-sm text-red-600 mt-1 font-medium flex items-center gap-1">
          <span className="inline-block w-1 h-1 bg-red-600 rounded-full"></span>
          {errors.sBrandLogo}
        </p>
      )}
    </div>
  );
};

const StoreSetupStep = ({ form, setForm, options, errors, onBlur }) => {
  const { storeTypes = [] } = options;
  const [selectedStoreId, setSelectedStoreId] = useState(() => {
    if (form.withSellerApp === "No") {
      // Find "Custom" store ID
      const customStore = storeTypes.find((s) => s.name === "Custom");
      return customStore?.iStoreTypeId || null;
    }
    return storeTypes[0]?.iStoreTypeId || null;
  });


  const { npType } = form;

  const labelMap = {
    store_url: "Store URL",
    access_token: "Access Token",
    admin_access_token: "Admin Token",
    admin: "Admin Email",
    password: "Password",
    token: "API Token",
    bap_id: npType === "Buyer App" ? "BAP ID" : "BPP ID",
    bap_url: npType === "Buyer App" ? "BAP URL" : "BPP URL",
  };
  const typeMap = {
    password: "password",
    admin: "email",
  };

  const selectedConfig = useMemo(
    () => storeTypes.find((s) => s.iStoreTypeId === selectedStoreId),
    [storeTypes, selectedStoreId]
  );


  const configKeys = selectedConfig?.configurationKeys || [];

  const handleInputChange = (e) => {
    const { name, value } = e.target;

    setForm((prev) => {
      let updatedForm = { ...prev, [name]: value };

      // If store type changes, reset and populate storeKeys
      if (name === "storeTypeId") {
        const selectedType = storeTypes.find(
          (type) => String(type.iStoreTypeId) === String(value)
        );

        updatedForm.storeKeys =
          selectedType?.configurationKeys?.map((key) => ({
            key,
            value: "",
          })) || [];
      }

      return updatedForm;
    });
  };

  const handleStoreConfigChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => {
      const existingIndex = (prev.storeKeys || []).findIndex(
        (item) => item.key === name
      );
      const updatedStoreKeys = [...(prev.storeKeys || [])];

      if (existingIndex > -1) {
        updatedStoreKeys[existingIndex] = {
          ...updatedStoreKeys[existingIndex],
          value,
        };
      } else {
        updatedStoreKeys.push({ key: name, value });
      }

      return {
        ...prev,
        storeKeys: updatedStoreKeys,
      };
    });
  };

  const handleLogoChange = (e) => {
    const file = e.target.files?.[0] || null;
    setForm((prev) => ({ ...prev, sBrandLogo: file }));
  };

  useEffect(() => {
    const selectedType = storeTypes.find(
      (s) => s.iStoreTypeId === selectedStoreId
    );

    setForm((prev) => ({
      ...prev,
      storeTypeId: selectedStoreId,
      storeKeys: selectedType?.configurationKeys?.length
        ? (prev.storeKeys || []) // keep values if config keys exist (default to [])
        : [], // clear if no config keys
    }));
  }, [selectedStoreId, storeTypes, setForm]);

  if (!storeTypes.length) {
    return <div>Loading store configurations...</div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex border-b border-gray-200 mb-4 overflow-x-auto">
        {storeTypes
          .filter((store) => {
            if (form.npType === "Buyer App") {
              return store.name === "Custom";
            }
            if (form.withSellerApp === "No") {
              return store.name === "Custom";
            }
            return true;
          })
          .map((store) => (
            <button
              type="button"
              key={store.iStoreTypeId}
              onClick={() => setSelectedStoreId(store.iStoreTypeId)}
              className={`flex-shrink-0 flex items-center px-4 py-2 text-sm font-medium border-b-2 transition-all duration-200 ${
                selectedStoreId === store.iStoreTypeId
                  ? "text-blue-600 border-blue-500"
                  : "text-gray-500 border-transparent hover:text-gray-700 hover:border-gray-300"
              }`}
            >
              <img
                src={store.logo}
                alt={store.name}
                className="h-5 w-5 mr-2 rounded-full"
              />
              {store.name}
            </button>
          ))}
      </div>


      {/* 🔹 Logo uploader */}
      <LogoUploader
        selectedFile={form.sBrandLogo}
        onChange={handleLogoChange}
        errors={errors}
        onBlur={() => onBlur("sBrandLogo")}
      />

      {/* 🔹 Brand Name */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Brand Name <span className="text-red-500">*</span>
        </label>
        <input
          name="brandName"
          value={form.brandName || ""}
          onChange={handleInputChange}
          onBlur={() => onBlur("brandName")}
          placeholder="Enter Brand Name"
          className={`w-full px-4 py-2 border ${errors?.brandName ? "border-red-500" : "border-gray-300"} rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200`}
        />
        {errors?.brandName && (
          <p className="text-sm text-red-600 mt-1 font-medium flex items-center gap-1">
            <span className="inline-block w-1 h-1 bg-red-600 rounded-full"></span>
            {errors.brandName}
          </p>
        )}
      </div>

      {selectedConfig?.name !== "Custom" && 
      form.npType === "Seller App" && (
        <div>
          {configKeys.length > 0 && (
            <div className="p-4 border rounded-lg space-y-4">
              <h3 className="text-md font-semibold text-gray-800">
                
                {selectedConfig?.name} Configuration
              </h3>

              {configKeys.map((key) => (
                  <div key={key}>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {labelMap[key] || key}<span className="text-red-500">*</span>
                  </label>
                  <input
                    type={typeMap[key] || "text"}
                    required
                    name={key}
                    value={
                      form?.storeKeys?.find((item) => item.key === key)
                        ?.value || ""
                    }
                    onChange={handleStoreConfigChange}
                    onBlur={() => onBlur(key)}
                    placeholder={`Enter ${labelMap[key] || key}`}
                    className={`w-full px-4 py-2 border ${
                      errors?.storeKeys?.[key]
                        ? "border-red-500"
                        : "border-gray-300"
                    } rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200`}
                  />
                  {errors?.storeKeys?.[key] && (
                    <p className="text-red-600 text-sm mt-1 font-medium flex items-center gap-1">
                      <span className="inline-block w-1 h-1 bg-red-600 rounded-full"></span>
                      {errors?.storeKeys?.[key]}
                    </p>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default StoreSetupStep;
